/**
 * Geometry shared by the assistant launcher and panel.
 *
 * These values are derived from one another: the panel sits above the launcher,
 * and the panel's height is whatever the viewport has left once its offset and
 * top inset are taken out. They lived as independent literals in two files,
 * which meant moving the launcher silently misaligned the panel with nothing to
 * catch it. Deriving them here makes that impossible.
 */

/** Square launcher, sized to read as the Gate Check app icon. */
export const LAUNCHER_SIZE = 56;

/** Distance from the right edge of the viewport. */
export const EDGE_INSET = { xs: 12, sm: 24 } as const;

/**
 * Vertical space reserved *below* the launcher for Pendo's Resource Center
 * badge, which is also pinned bottom-right and would otherwise sit on top of
 * the launcher and make it unclickable.
 *
 * Pendo's badge position is configured in the Pendo dashboard, not here, so
 * this is a reservation rather than a measurement — we cannot read it and it
 * can change without a deploy. It is deliberately generous: if Pendo's badge
 * shrinks or moves away the launcher floats slightly high, which is cosmetic,
 * whereas too small a reservation makes the launcher unclickable. Given we
 * cannot enforce the other side, the failure we choose is the harmless one.
 */
export const PENDO_BADGE_CLEARANCE = 76;

/** Launcher offset from the bottom edge, clearing Pendo's badge. */
export const LAUNCHER_BOTTOM = EDGE_INSET.sm + PENDO_BADGE_CLEARANCE;

/** Breathing room between the top of the launcher and the bottom of the panel. */
const PANEL_GAP = { xs: 12, sm: 16 } as const;

/** Panel offset from the bottom edge, clearing the launcher beneath it. */
export const PANEL_BOTTOM = {
  xs: LAUNCHER_BOTTOM + LAUNCHER_SIZE + PANEL_GAP.xs,
  sm: LAUNCHER_BOTTOM + LAUNCHER_SIZE + PANEL_GAP.sm,
} as const;

/** Space left above the panel so it never runs to the top of the viewport. */
const PANEL_TOP_INSET = 44;

/**
 * Height available to the panel at each breakpoint, as a CSS expression.
 * Recomputed from the offsets above so raising the launcher cannot push the
 * panel off the top of the screen.
 */
export const PANEL_HEIGHT = {
  xs: `calc(100vh - ${PANEL_BOTTOM.xs + PANEL_TOP_INSET}px)`,
  sm: `calc(100vh - ${PANEL_BOTTOM.sm + PANEL_TOP_INSET}px)`,
} as const;
